import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../api/auth';

const ClassDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // ===== States principaux =====
  const [classData, setClassData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('students');

  // ===== States secondaires =====
  const [schedules, setSchedules] = useState([]);
  const [schedulesLoading, setSchedulesLoading] = useState(false);

  const [results, setResults] = useState([]);
  const [resultsLoading, setResultsLoading] = useState(false);

  const [absences, setAbsences] = useState([]);
  const [absencesLoading, setAbsencesLoading] = useState(false);

  // ===== Chargement initial =====
  useEffect(() => {
    if (!id) return;
    fetchClass();
    fetchSchedules();
    fetchResults();
    fetchAbsences();
    // eslint-disable-next-line
  }, [id]);

  // ===== Fetchers =====
  const fetchClass = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const res = await api.get(`/classes/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClassData(res.data);
    } catch (err) {
      console.error('Erreur classe:', err);
      setError(err.response?.data?.error || 'Erreur lors du chargement de la classe');
    } finally {
      setLoading(false);
    }
  };

  const fetchSchedules = async () => {
    try {
      setSchedulesLoading(true);
      const token = localStorage.getItem('token');
      const res = await api.get(`/admin/schedules?class_id=${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = res.data;
      setSchedules(Array.isArray(data) ? data : (data.data || []));
    } catch (err) {
      console.error('Erreur schedules:', err);
      setSchedules([]);
    } finally {
      setSchedulesLoading(false);
    }
  };

  const fetchResults = async () => {
    try {
      setResultsLoading(true);
      const token = localStorage.getItem('token');
      const res = await api.get(`/class-results/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResults(res.data.students || []);
    } catch (err) {
      console.error('Erreur results:', err);
      setResults([]);
    } finally {
      setResultsLoading(false);
    }
  };

  const fetchAbsences = async () => {
    try {
      setAbsencesLoading(true);
      const token = localStorage.getItem('token');
      const res = await api.get(`/admin/absences?class_id=${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = res.data;
      setAbsences(Array.isArray(data) ? data : (data.data || []));
    } catch (err) {
      console.error('Erreur absences:', err);
      setAbsences([]);
    } finally {
      setAbsencesLoading(false);
    }
  };

  // ===== Écrans de chargement / erreur =====
  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error || !classData) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4">
          <p className="text-red-700">❌ {error || 'Classe introuvable'}</p>
          <button
            onClick={() => navigate('/admin/classes')}
            className="mt-3 text-sm text-red-600 underline"
          >
            ← Retour aux classes
          </button>
        </div>
      </div>
    );
  }

  // ===== Variables dérivées =====
  const students = classData.students || [];
  const assignments = classData.assignments || [];
  const studentCount = classData.students_count || students.length;
  const maxStudents = classData.max_students || 50;
  const ratio = maxStudents > 0 ? Math.min(100, Math.round((studentCount / maxStudents) * 100)) : 0;
  const classTeacherName =
    classData.class_teacher?.name ||
    classData.classTeacher?.name ||
    'Non assigné';

  const tabs = [
    { id: 'students', label: '👨‍🎓 Élèves', count: studentCount },
    { id: 'teachers', label: '👨‍🏫 Professeurs', count: assignments.length },
    { id: 'subjects', label: '📚 Matières', count: assignments.length },
    { id: 'schedule', label: '📅 Emploi du temps', count: schedules.length },
    { id: 'results', label: '📊 Résultats', count: results.length },
    { id: 'absences', label: '🚨 Absences', count: absences.length },
  ];

  return (
    <div className="space-y-6">
      {/* ===== Header ===== */}
      <div>
        <button
          onClick={() => navigate('/admin/classes')}
          className="text-sm text-gray-500 hover:text-[#E67E22] mb-2 flex items-center space-x-1"
        >
          <span>←</span>
          <span>Retour aux classes</span>
        </button>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-[#2C3E50]">🏫 {classData.name}</h1>
            <p className="text-gray-500">
              {classData.level}
              {classData.section ? ` • Section ${classData.section}` : ''}
              {classData.academic_year ? ` • ${classData.academic_year}` : ''}
            </p>
          </div>
        </div>
      </div>

      {/* ===== Info card ===== */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-gray-500 mb-1">👥 Effectif</p>
            <p className="text-lg font-bold text-[#2C3E50]">
              {studentCount}/{maxStudents}
            </p>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
              <div
                className="bg-[#E67E22] h-2 rounded-full"
                style={{ width: `${ratio}%` }}
              ></div>
            </div>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">👨‍🏫 Prof principal</p>
            <p className="text-lg font-bold text-[#2C3E50]">{classTeacherName}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">📚 Matières</p>
            <p className="text-lg font-bold text-[#2C3E50]">{assignments.length}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">📅 Année scolaire</p>
            <p className="text-lg font-bold text-[#2C3E50]">
              {classData.academic_year || '-'}
            </p>
          </div>
        </div>
      </div>

      {/* ===== Tabs ===== */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="flex border-b border-gray-200 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition ${
                activeTab === tab.id
                  ? 'text-[#E67E22] border-b-2 border-[#E67E22]'
                  : 'text-gray-600 hover:text-[#E67E22]'
              }`}
            >
              {tab.label}
              {tab.count !== null && (
                <span className="ml-2 text-xs bg-gray-100 px-2 py-0.5 rounded-full">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="p-6">
          {/* ========================================================= */}
          {/* Onglet ÉLÈVES                                             */}
          {/* ========================================================= */}
          {activeTab === 'students' && (
            <div>
              {students.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <p className="text-4xl mb-3">📭</p>
                  <p className="text-lg font-medium mb-2">Aucun élève dans cette classe</p>
                  <p className="text-sm">Ajoutez des élèves pour les voir apparaître ici.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Nom</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Matricule</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Sexe</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.map((s) => (
                        <tr key={s.id} className="border-t hover:bg-gray-50">
                          <td className="py-3 px-4 font-medium">
                            {s.user?.name || s.name || 'Élève'}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600">
                            {s.matricule || '-'}
                          </td>
                          <td className="py-3 px-4 text-sm">
                            {s.sexe === 'M' ? '👦 M' : s.sexe === 'F' ? '👧 F' : '—'}
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                              ✅ Actif
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ========================================================= */}
          {/* Onglet PROFESSEURS                                        */}
          {/* ========================================================= */}
          {activeTab === 'teachers' && (
            <div>
              {assignments.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <p className="text-4xl mb-3">📭</p>
                  <p className="text-lg font-medium mb-2">Aucun professeur affecté</p>
                  <p className="text-sm">Affectez des professeurs à cette classe pour les voir ici.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Professeur</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Matière</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Rôle</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assignments.map((a) => {
                        const isPrincipal = classData.class_teacher_id === a.teacher_id;
                        return (
                          <tr key={a.id} className="border-t hover:bg-gray-50">
                            <td className="py-3 px-4 font-medium">
                              {a.teacher?.name || 'Prof'}
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-600">
                              {a.subject?.name || '-'}
                            </td>
                            <td className="py-3 px-4">
                              {isPrincipal ? (
                                <span className="text-xs bg-orange-100 text-[#E67E22] px-2 py-1 rounded-full font-medium">
                                  👑 Prof principal
                                </span>
                              ) : (
                                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                                  Enseignant
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ========================================================= */}
          {/* Onglet MATIÈRES                                           */}
          {/* ========================================================= */}
          {activeTab === 'subjects' && (
            <div>
              {assignments.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <p className="text-4xl mb-3">📭</p>
                  <p className="text-lg font-medium mb-2">Aucune matière enseignée</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {assignments.map((a) => (
                    <div key={a.id} className="border rounded-lg p-4">
                      <p className="font-bold text-[#2C3E50]">
                        📚 {a.subject?.name || 'Matière'}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        👨‍🏫 {a.teacher?.name || 'Prof non assigné'}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ========================================================= */}
          {/* Onglet EMPLOI DU TEMPS                                    */}
          {/* ========================================================= */}
          {activeTab === 'schedule' && (
            <div>
              {schedulesLoading ? (
                <div className="text-center py-12 text-gray-500">Chargement...</div>
              ) : schedules.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <p className="text-4xl mb-3">📅</p>
                  <p className="text-lg font-medium mb-2">Aucun emploi du temps</p>
                  <p className="text-sm">
                    Ajoutez un emploi du temps depuis <strong>Admin → Emploi du temps</strong>.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Jour</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Heure début</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Heure fin</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Matière</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Professeur</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Salle</th>
                      </tr>
                    </thead>
                    <tbody>
                      {schedules.map((s) => (
                        <tr key={s.id} className="border-t hover:bg-gray-50">
                          <td className="py-3 px-4 font-medium capitalize">
                            {s.day_of_week || s.day || '-'}
                          </td>
                          <td className="py-3 px-4 text-sm">
                            {s.start_time ? String(s.start_time).substring(0, 5) : '-'}
                          </td>
                          <td className="py-3 px-4 text-sm">
                            {s.end_time ? String(s.end_time).substring(0, 5) : '-'}
                          </td>
                          <td className="py-3 px-4 text-sm">
                            {s.subject?.name || s.subject_name || '-'}
                          </td>
                          <td className="py-3 px-4 text-sm">
                            {s.teacher?.name || s.teacher_name || '-'}
                          </td>
                          <td className="py-3 px-4 text-sm">
                            {s.room || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ========================================================= */}
          {/* Onglet RÉSULTATS                                          */}
          {/* ========================================================= */}
          {activeTab === 'results' && (
            <div>
              {resultsLoading ? (
                <div className="text-center py-12 text-gray-500">Chargement...</div>
              ) : results.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <p className="text-4xl mb-3">📊</p>
                  <p className="text-lg font-medium mb-2">Aucun résultat</p>
                  <p className="text-sm">
                    Les résultats apparaîtront une fois les notes saisies et validées.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Rang</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Élève</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Matricule</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Moyenne</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Absences</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Retards</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.map((r) => (
                        <tr key={r.student_id} className="border-t hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full font-medium">
                              #{r.rank}
                            </span>
                          </td>
                          <td className="py-3 px-4 font-medium">{r.student_name}</td>
                          <td className="py-3 px-4 text-sm text-gray-600">{r.matricule || '-'}</td>
                          <td className="py-3 px-4">
                            <span className={`font-bold ${
                              r.general_average >= 14 ? 'text-green-600' :
                              r.general_average >= 10 ? 'text-yellow-600' :
                              'text-red-600'
                            }`}>
                              {r.general_average}/20
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm">{r.absences_count || 0}</td>
                          <td className="py-3 px-4 text-sm">{r.lates_count || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ========================================================= */}
          {/* Onglet ABSENCES                                           */}
          {/* ========================================================= */}
          {activeTab === 'absences' && (
            <div>
              {absencesLoading ? (
                <div className="text-center py-12 text-gray-500">Chargement...</div>
              ) : absences.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <p className="text-4xl mb-3">🚨</p>
                  <p className="text-lg font-medium mb-2">Aucune absence</p>
                  <p className="text-sm">Les absences apparaîtront ici une fois enregistrées.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Date</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Élève</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Type</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Durée</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Justifiée</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Motif</th>
                      </tr>
                    </thead>
                    <tbody>
                      {absences.map((a) => (
                        <tr key={a.id} className="border-t hover:bg-gray-50">
                          <td className="py-3 px-4 text-sm">{a.date || '-'}</td>
                          <td className="py-3 px-4 font-medium">{a.student_name || '-'}</td>
                          <td className="py-3 px-4">
                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                              a.type === 'absence'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {a.type === 'absence' ? '❌ Absence' : '⏰ Retard'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm">{a.duration_minutes || 0} min</td>
                          <td className="py-3 px-4">
                            {a.is_justified ? (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                                ✅ Oui
                              </span>
                            ) : (
                              <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                                ⚪ Non
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600">{a.reason || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClassDetail;